import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import BlacklistCard from '@/components/feature/BlacklistCard';
import StateBlock from '@/components/base/StateBlock';
import { searchContent } from '@/utils/content';

const hotKeywords = ['今日黑榜', '星轨联赛', '极光杯', '排名分析', '雷霆竞技场', '选手表现'];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const typeParam = (searchParams.get('type') as 'all' | 'blacklist' | 'news') || 'all';

  const [input, setInput] = useState(query);
  const [loading, setLoading] = useState(false);

  useDocumentMeta({
    title: query ? `搜索“${query}” - 51每日大赛` : '搜索 - 51每日大赛',
    description:
      '在51每日大赛中搜索赛事黑榜与赛事资讯，支持关键词匹配选手、赛事、战队与标题内容，快速定位你关心的排名与报道。',
    keywords: '搜索,51每日大赛,今日黑榜,赛事资讯,排名查询',
  });

  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [query, typeParam]);

  const results = useMemo(() => searchContent(query, { type: typeParam }), [query, typeParam]);

  const runSearch = (q: string) => {
    const next = new URLSearchParams(searchParams);
    if (q.trim()) {
      next.set('q', q.trim());
    } else {
      next.delete('q');
    }
    setSearchParams(next);
  };

  const setType = (type: 'all' | 'blacklist' | 'news') => {
    const next = new URLSearchParams(searchParams);
    if (type === 'all') {
      next.delete('type');
    } else {
      next.set('type', type);
    }
    setSearchParams(next);
  };

  const typeFilters: { key: 'all' | 'blacklist' | 'news'; label: string; icon: string }[] = [
    { key: 'all', label: '全部', icon: 'ri-apps-2-line' },
    { key: 'blacklist', label: '黑榜', icon: 'ri-alarm-warning-line' },
    { key: 'news', label: '赛事资讯', icon: 'ri-newspaper-line' },
  ];

  const blackResults = results.filter((r) => r.type === 'blacklist');
  const newsResults = results.filter((r) => r.type === 'news');

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '搜索' }]} />

      <header className="mt-5">
        <h1 className="text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
          全站搜索
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600">
          输入关键词，快速匹配赛事黑榜与赛事资讯内容。
          <span className="ml-1 text-foreground-400">演示内容，实际数据接入待开发。</span>
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          runSearch(input);
        }}
        className="mt-6 flex flex-col sm:flex-row gap-3"
      >
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索黑榜选手、赛事名称或资讯标题"
            className="w-full pl-11 pr-4 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
          />
        </div>
        <button
          type="submit"
          className="px-6 py-3 rounded-md bg-primary-600 text-background-50 text-sm font-bold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-foreground-500 mr-1">按类别过滤：</span>
        {typeFilters.map((filter) => (
          <button
            key={filter.key}
            type="button"
            onClick={() => setType(filter.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
              typeParam === filter.key
                ? 'bg-primary-600 text-background-50'
                : 'bg-background-200 text-foreground-600 hover:text-foreground-800'
            }`}
          >
            <i className={filter.icon}></i>
            {filter.label}
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-foreground-600">
          {query ? (
            <>
              关键词 <span className="font-bold text-foreground-900">“{query}”</span> 共找到{' '}
              <span className="font-bold text-primary-700">{results.length}</span> 条结果
            </>
          ) : (
            <>
              共 <span className="font-bold text-primary-700">{results.length}</span> 条内容，可直接浏览或输入关键词检索
            </>
          )}
        </p>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="rounded-lg border border-background-200 bg-background-50">
            <StateBlock type="loading" title="正在搜索…" description="正在为你匹配相关黑榜与资讯内容" />
          </div>
        ) : results.length > 0 ? (
          <div className="flex flex-col gap-8">
            {blackResults.length > 0 && (
              <section>
                <h2 className="text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-alarm-warning-line text-accent-600"></i>
                  黑榜（{blackResults.length}）
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {blackResults.map((item) => (
                    <BlacklistCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}
            {newsResults.length > 0 && (
              <section>
                <h2 className="text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-newspaper-line text-primary-600"></i>
                  赛事资讯（{newsResults.length}）
                </h2>
                <div className="flex flex-col gap-4">
                  {newsResults.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50 pb-10">
            <StateBlock
              type="empty"
              title="没有找到匹配的内容"
              description="试试更换关键词，或从下方热搜词与分类入口重新开始。"
            />
            <div className="px-5">
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {hotKeywords.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => runSearch(word)}
                    className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {word}
                  </button>
                ))}
              </div>
              <div className="flex justify-center gap-3 flex-wrap">
                <Link
                  to="/category/blacklist"
                  className="px-5 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部黑榜
                </Link>
                <Link
                  to="/category/news"
                  className="px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部资讯
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {!query && (
        <div className="mt-8 rounded-lg border border-background-200 bg-background-200/40 p-5">
          <h2 className="text-sm font-bold text-foreground-800 mb-3">热门搜索</h2>
          <div className="flex flex-wrap gap-2">
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => runSearch(word)}
                className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}